import React, {useEffect, useLayoutEffect, useRef, useState} from 'react'
import cx from 'classnames'
import styles from './FontCard.module.css'
import Card from '../../../../components/Card'
import {Font} from '../../../../types/Font'

const MAX_FONT_PX = 36
const MAX_CURVED_FONT_PX = 48
const MIN_FONT_PX = 10
const STEP_PX = 2

function fitFontSize(
    mode: 'normal' | 'curved',
    el: HTMLDivElement | null,
    textEl: SVGTextElement | null,
    pathEl: SVGPathElement | null
): number {
    const maxPx = mode === 'curved' ? MAX_CURVED_FONT_PX : MAX_FONT_PX
    let px = maxPx

    if (mode === 'curved' && textEl && pathEl) {
        const pathLength = pathEl.getTotalLength()
        textEl.style.fontSize = `${px}px`
        while (textEl.getComputedTextLength() > pathLength && px > MIN_FONT_PX) {
            px -= STEP_PX
            textEl.style.fontSize = `${px}px`
        }
        return px
    }

    if (mode === 'normal' && el) {
        el.style.fontSize = `${px}px`
        while (el.scrollWidth > el.clientWidth && el.clientWidth > 0 && px > MIN_FONT_PX) {
            px -= STEP_PX
            el.style.fontSize = `${px}px`
        }
        return px
    }

    return maxPx
}

const FontCard: React.FC<{
    font: Font
    onClick?: (font: Font) => void
    chosen: boolean
    style?: 'normal' | 'curved'
    previewText?: string
}> = ({font, onClick, chosen, style = 'normal', previewText = 'workwear'}) => {
    const previewRef = useRef<HTMLDivElement>(null)
    const curvedTextRef = useRef<SVGTextElement>(null)
    const curvedPathRef = useRef<SVGPathElement>(null)
    const [fontSizePx, setFontSizePx] = useState(MAX_FONT_PX)
    const [fontLoadCount, setFontLoadCount] = useState(0)

    useEffect(() => {
        if (!font.path) return
        const fontFace = new FontFace(font.name, `url(${font.path})`)
        fontFace.load().then((f) => {
            document.fonts.add(f)
            setFontLoadCount((c) => c + 1)
        }).catch((error) => {
            console.error(`Failed to load font ${font.name}:`, error)
        })
    }, [font])

    useLayoutEffect(() => {
        const px = fitFontSize(
            style,
            previewRef.current,
            curvedTextRef.current,
            curvedPathRef.current
        )
        setFontSizePx(px)
    }, [style, previewText, font.name, fontLoadCount])

    return (
        <Card
            key={font.id}
            onClick={onClick ? () => onClick(font) : undefined}
            hoverEffect={false}
        >
            <div className={cx(styles['card-inner-wrapper'])}>
                {style === 'curved' ? (
                    <svg viewBox="0 0 300 120" className={cx(styles['font-preview-svg'])}>
                        <title>{font.name}</title>
                        <defs>
                            <path
                                ref={curvedPathRef}
                                id={`curve-${font.id}`}
                                d="M 20 80 Q 150 10 280 80"
                            />
                        </defs>
                        <text
                            ref={curvedTextRef}
                            className={cx(styles['font-preview-curved'])}
                            style={{fontSize: `${fontSizePx}px`}}
                        >
                            <textPath style={{fontFamily: font.name}} href={`#curve-${font.id}`} startOffset="50%"
                                      textAnchor="middle" dominantBaseline="middle">
                                {previewText}
                            </textPath>
                        </text>
                    </svg>
                ) : (
                    <div
                        ref={previewRef}
                        title={font.name}
                        className={cx(styles['font-preview'])}
                        style={{fontFamily: font.name, fontSize: `${fontSizePx}px`}}
                    >
                        {previewText}
                    </div>
                )}
            </div>
        </Card>
    )
}

export default FontCard
